/**
 * 일별 언급률 추이 — 점 추정 + 표본 수. 문구·probe_version 이 바뀐 날은 세로선.
 * ★ **실측이 실선(메인)**이고 온보딩 표시값이 점선이다. 95% 구간 띠는 걷었다(운영자 결정 2026-11-03 —
 *   질문 상세 카드도 실측이 메인이다).
 * n < 10 인 날은 점을 흐리게 — 선이 이어진다고 추세가 있는 것이 아니다 (F-12).
 *
 * ★ **운영자 전용이다**(`query-detail.tsx` 가 유일한 소비자). 온보딩 쓰로틀링이 걸린 날은
 *   **실측 실선 옆에 표시값을 점선으로 겹쳐 그린다** — 카드(`RateCell` 의 "온보딩 NN%")와 같은 규율을
 *   그림에 옮긴 것이고, 운영자는 그 둘의 간격으로 수집 품질을 판단한다
 *   (260921-report-snapshot-and-onboarding-throttle "운영자 화면은 실측과 표시값을 나란히 본다").
 *   고객 화면(`TrendArt`)에는 실측이 애초에 내려가지 않는다.
 */
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { Rate } from '@geo-admin/shared/contract/common.ts';
import { MIN_SAMPLES } from '../lib/format.ts';

interface Point {
  date: string;
  attempts: number;
  mention: Rate;
  /** 쓰로틀링 전 실측 — 계약상 admin 에게만 온다 */
  raw: Rate | null;
  /** 그날의 온보딩 계수 — 날마다 다르다(노이즈). 계약상 admin 에게만 */
  factor: number | null;
}

const pct = (r: Rate) => (r.n > 0 ? Math.round((r.k / r.n) * 100) : null);

export function RateTrend({
  daily,
  markers,
}: {
  daily: Point[];
  markers: { date: string; kind: string; value: string }[];
}) {
  const data = daily.map((d) => ({
    date: d.date.slice(5),
    full: d.date,
    /** 메인 선 — 실측이 있으면 실측 */
    rate: pct(d.raw ?? d.mention),
    /** 온보딩 표시값 */
    shownRate: pct(d.mention),
    n: d.mention.n,
    k: d.mention.k,
    rawK: d.raw ? d.raw.k : null,
    factor: d.factor,
    insufficient: d.mention.n < MIN_SAMPLES,
  }));
  // 계수가 1 이면 두 선이 포개진다 — 같은 숫자를 두 번 그리지 않는다 (RateCell 과 같은 판정식)
  const hasRaw = data.some((p) => p.rawK !== null && p.rawK !== p.k);
  return (
    <div className="h-64 w-full">
      <ResponsiveContainer>
        <ComposedChart data={data} margin={{ top: 10, right: 16, bottom: 0, left: -10 }}>
          <CartesianGrid stroke="#e2e8f0" vertical={false} />
          <XAxis dataKey="date" tick={{ fontSize: 12 }} />
          <YAxis domain={[0, 100]} unit="%" tick={{ fontSize: 12 }} />
          <Tooltip
            content={({ active, payload }) => {
              if (!active) return null;
              const p = payload?.[0]?.payload as (typeof data)[number] | undefined;
              if (!p) return null;
              const mainK = p.rawK ?? p.k;
              const main =
                p.n === 0 ? '표본 없음' : `${mainK}/${p.n}${p.insufficient ? ' · 표본 부족' : ` · ${p.rate}%`}`;
              return (
                <div className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-slate-700 text-xs shadow-sm">
                  <div className="text-slate-400">{p.full}</div>
                  <div className="tabular-nums">
                    {p.rawK !== null ? '실측' : '언급률'} {main}
                  </div>
                  {p.rawK !== null && p.rawK !== p.k ? (
                    <div className="tabular-nums text-slate-500">
                      온보딩 {p.k}/{p.n}
                      {p.insufficient ? '' : ` · ${p.shownRate}%`}
                    </div>
                  ) : null}
                  {p.factor !== null && p.factor < 1 ? (
                    <div className="text-slate-400 tabular-nums">계수 ×{p.factor.toFixed(3)}</div>
                  ) : null}
                </div>
              );
            }}
          />
          {/* 선이 하나뿐일 때 범례는 군더더기다 — 차트 높이에서 28px 만 먹는다 */}
          {hasRaw ? (
            <Legend verticalAlign="top" height={28} iconType="plainline" wrapperStyle={{ fontSize: 12 }} />
          ) : null}
          {/* 온보딩 표시값을 먼저 — 실측 실선이 그 위에 온다 */}
          {hasRaw ? (
            <Line
              name="온보딩"
              dataKey="shownRate"
              stroke="#94a3b8"
              strokeWidth={2}
              strokeDasharray="4 3"
              dot={false}
              isAnimationActive={false}
              connectNulls={false}
            />
          ) : null}
          <Line
            name={hasRaw ? '실측' : '언급률'}
            dataKey="rate"
            stroke="var(--color-brand)"
            strokeWidth={2}
            isAnimationActive={false}
            connectNulls={false}
            dot={(props: {
              cx?: number;
              cy?: number;
              payload?: { insufficient: boolean; rate: number | null };
              index?: number;
            }) =>
              props.payload?.rate === null || props.cx === undefined || props.cy === undefined ? (
                <g key={props.index} />
              ) : (
                <circle
                  key={props.index}
                  cx={props.cx}
                  cy={props.cy}
                  r={4}
                  fill={props.payload?.insufficient ? '#fff' : 'var(--color-brand)'}
                  stroke="var(--color-brand)"
                />
              )
            }
          />
          {markers.map((m) => (
            <ReferenceLine
              key={`${m.date}-${m.kind}-${m.value}`}
              x={m.date.slice(5)}
              stroke={m.kind === 'query_text' ? '#f59e0b' : '#94a3b8'}
              strokeDasharray="4 3"
              label={{
                value: m.kind === 'query_text' ? '문구 변경' : '측정기 변경',
                fontSize: 11,
                position: 'insideTop',
              }}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
